/**
 * WHO Child Growth Standards — LMS reference tables.
 *
 * The Box-Cox (LMS) method encodes a skewed reference distribution as three
 * parameters per age: L (skew), M (median), S (coefficient of variation).
 * Value X at z-score Z is:
 *   L != 0:  X = M × (1 + L × S × Z)^(1/L)
 *   L == 0:  X = M × exp(S × Z)
 *
 * Common percentile z-scores:
 *   3rd  = -1.881
 *   15th = -1.036
 *   50th =  0
 *   85th =  1.036
 *   97th =  1.881
 *
 * Values below are from WHO Child Growth Standards, published 2006, monthly
 * age (0..24 months) for weight-for-age. Boys and girls tables both included.
 *
 * NOTE for pediatric decisions: charts derived from these tables are for parent
 * TRENDING, not clinical diagnosis. Pediatricians should use WHO Anthro or CDC
 * tools directly. Documented in the UI.
 */

export interface LmsRow {
  months: number
  L: number
  M: number
  S: number
}

/** WHO Weight-for-age (kg), BOYS, 0-24 months */
export const WHO_WFA_BOYS: LmsRow[] = [
  { months: 0,  L: 0.3487,  M: 3.3464,  S: 0.14602 },
  { months: 1,  L: 0.2297,  M: 4.4709,  S: 0.13395 },
  { months: 2,  L: 0.1970,  M: 5.5675,  S: 0.12385 },
  { months: 3,  L: 0.1738,  M: 6.3762,  S: 0.11727 },
  { months: 4,  L: 0.1553,  M: 7.0023,  S: 0.11316 },
  { months: 5,  L: 0.1395,  M: 7.5105,  S: 0.11080 },
  { months: 6,  L: 0.1257,  M: 7.9340,  S: 0.10958 },
  { months: 7,  L: 0.1134,  M: 8.2970,  S: 0.10902 },
  { months: 8,  L: 0.1021,  M: 8.6151,  S: 0.10882 },
  { months: 9,  L: 0.0917,  M: 8.9014,  S: 0.10881 },
  { months: 10, L: 0.0820,  M: 9.1649,  S: 0.10891 },
  { months: 11, L: 0.0730,  M: 9.4122,  S: 0.10906 },
  { months: 12, L: 0.0644,  M: 9.6479,  S: 0.10925 },
  { months: 13, L: 0.0563,  M: 9.8749,  S: 0.10949 },
  { months: 14, L: 0.0487,  M: 10.0953, S: 0.10976 },
  { months: 15, L: 0.0413,  M: 10.3108, S: 0.11007 },
  { months: 16, L: 0.0343,  M: 10.5228, S: 0.11041 },
  { months: 17, L: 0.0275,  M: 10.7319, S: 0.11079 },
  { months: 18, L: 0.0211,  M: 10.9385, S: 0.11119 },
  { months: 19, L: 0.0148,  M: 11.1430, S: 0.11164 },
  { months: 20, L: 0.0087,  M: 11.3462, S: 0.11211 },
  { months: 21, L: 0.0029,  M: 11.5486, S: 0.11261 },
  { months: 22, L: -0.0028, M: 11.7504, S: 0.11314 },
  { months: 23, L: -0.0083, M: 11.9514, S: 0.11369 },
  { months: 24, L: -0.0137, M: 12.1515, S: 0.11426 },
]

/** WHO Length-for-age (cm, supine — 0-24 months uses length not standing height), BOYS */
export const WHO_LFA_BOYS: LmsRow[] = [
  { months: 0,  L: 1, M: 49.8842, S: 0.03795 },
  { months: 1,  L: 1, M: 54.7244, S: 0.03557 },
  { months: 2,  L: 1, M: 58.4249, S: 0.03424 },
  { months: 3,  L: 1, M: 61.4292, S: 0.03328 },
  { months: 4,  L: 1, M: 63.8860, S: 0.03257 },
  { months: 5,  L: 1, M: 65.9026, S: 0.03204 },
  { months: 6,  L: 1, M: 67.6236, S: 0.03165 },
  { months: 7,  L: 1, M: 69.1645, S: 0.03139 },
  { months: 8,  L: 1, M: 70.5994, S: 0.03124 },
  { months: 9,  L: 1, M: 71.9687, S: 0.03117 },
  { months: 10, L: 1, M: 73.2812, S: 0.03118 },
  { months: 11, L: 1, M: 74.5388, S: 0.03125 },
  { months: 12, L: 1, M: 75.7488, S: 0.03137 },
  { months: 13, L: 1, M: 76.9186, S: 0.03154 },
  { months: 14, L: 1, M: 78.0497, S: 0.03174 },
  { months: 15, L: 1, M: 79.1458, S: 0.03197 },
  { months: 16, L: 1, M: 80.2113, S: 0.03222 },
  { months: 17, L: 1, M: 81.2487, S: 0.03250 },
  { months: 18, L: 1, M: 82.2587, S: 0.03279 },
  { months: 19, L: 1, M: 83.2418, S: 0.03310 },
  { months: 20, L: 1, M: 84.1996, S: 0.03342 },
  { months: 21, L: 1, M: 85.1348, S: 0.03376 },
  { months: 22, L: 1, M: 86.0477, S: 0.03410 },
  { months: 23, L: 1, M: 86.9410, S: 0.03445 },
  { months: 24, L: 1, M: 87.8161, S: 0.03479 },
]

/** WHO Length-for-age (cm), GIRLS, 0-24 months */
export const WHO_LFA_GIRLS: LmsRow[] = [
  { months: 0,  L: 1, M: 49.1477, S: 0.03790 },
  { months: 1,  L: 1, M: 53.6872, S: 0.03640 },
  { months: 2,  L: 1, M: 57.0673, S: 0.03568 },
  { months: 3,  L: 1, M: 59.8029, S: 0.03520 },
  { months: 4,  L: 1, M: 62.0899, S: 0.03486 },
  { months: 5,  L: 1, M: 64.0301, S: 0.03463 },
  { months: 6,  L: 1, M: 65.7311, S: 0.03448 },
  { months: 7,  L: 1, M: 67.2873, S: 0.03442 },
  { months: 8,  L: 1, M: 68.7498, S: 0.03442 },
  { months: 9,  L: 1, M: 70.1435, S: 0.03446 },
  { months: 10, L: 1, M: 71.4818, S: 0.03454 },
  { months: 11, L: 1, M: 72.7710, S: 0.03464 },
  { months: 12, L: 1, M: 74.0150, S: 0.03476 },
  { months: 13, L: 1, M: 75.2176, S: 0.03489 },
  { months: 14, L: 1, M: 76.3817, S: 0.03502 },
  { months: 15, L: 1, M: 77.5099, S: 0.03516 },
  { months: 16, L: 1, M: 78.6055, S: 0.03530 },
  { months: 17, L: 1, M: 79.6710, S: 0.03544 },
  { months: 18, L: 1, M: 80.7079, S: 0.03559 },
  { months: 19, L: 1, M: 81.7182, S: 0.03574 },
  { months: 20, L: 1, M: 82.7036, S: 0.03589 },
  { months: 21, L: 1, M: 83.6654, S: 0.03605 },
  { months: 22, L: 1, M: 84.6040, S: 0.03621 },
  { months: 23, L: 1, M: 85.5202, S: 0.03637 },
  { months: 24, L: 1, M: 86.4153, S: 0.03654 },
]

/** WHO Head-circumference-for-age (cm), BOYS, 0-24 months */
export const WHO_HCFA_BOYS: LmsRow[] = [
  { months: 0,  L: 1, M: 34.4618, S: 0.03686 },
  { months: 1,  L: 1, M: 37.2759, S: 0.03133 },
  { months: 2,  L: 1, M: 39.1285, S: 0.02997 },
  { months: 3,  L: 1, M: 40.5135, S: 0.02918 },
  { months: 4,  L: 1, M: 41.6317, S: 0.02868 },
  { months: 5,  L: 1, M: 42.5576, S: 0.02836 },
  { months: 6,  L: 1, M: 43.3306, S: 0.02817 },
  { months: 7,  L: 1, M: 43.9803, S: 0.02807 },
  { months: 8,  L: 1, M: 44.5300, S: 0.02802 },
  { months: 9,  L: 1, M: 45.0009, S: 0.02802 },
  { months: 10, L: 1, M: 45.4085, S: 0.02804 },
  { months: 11, L: 1, M: 45.7645, S: 0.02807 },
  { months: 12, L: 1, M: 46.0788, S: 0.02812 },
  { months: 13, L: 1, M: 46.3576, S: 0.02818 },
  { months: 14, L: 1, M: 46.6070, S: 0.02824 },
  { months: 15, L: 1, M: 46.8322, S: 0.02832 },
  { months: 16, L: 1, M: 47.0376, S: 0.02839 },
  { months: 17, L: 1, M: 47.2264, S: 0.02848 },
  { months: 18, L: 1, M: 47.4014, S: 0.02856 },
  { months: 19, L: 1, M: 47.5647, S: 0.02865 },
  { months: 20, L: 1, M: 47.7178, S: 0.02874 },
  { months: 21, L: 1, M: 47.8624, S: 0.02883 },
  { months: 22, L: 1, M: 47.9992, S: 0.02893 },
  { months: 23, L: 1, M: 48.1290, S: 0.02903 },
  { months: 24, L: 1, M: 48.2528, S: 0.02914 },
]

/** WHO Head-circumference-for-age (cm), GIRLS, 0-24 months */
export const WHO_HCFA_GIRLS: LmsRow[] = [
  { months: 0,  L: 1, M: 33.8787, S: 0.03496 },
  { months: 1,  L: 1, M: 36.5463, S: 0.03210 },
  { months: 2,  L: 1, M: 38.2521, S: 0.03089 },
  { months: 3,  L: 1, M: 39.5328, S: 0.03017 },
  { months: 4,  L: 1, M: 40.5817, S: 0.02969 },
  { months: 5,  L: 1, M: 41.4590, S: 0.02938 },
  { months: 6,  L: 1, M: 42.1948, S: 0.02918 },
  { months: 7,  L: 1, M: 42.8201, S: 0.02905 },
  { months: 8,  L: 1, M: 43.3585, S: 0.02897 },
  { months: 9,  L: 1, M: 43.8258, S: 0.02893 },
  { months: 10, L: 1, M: 44.2360, S: 0.02891 },
  { months: 11, L: 1, M: 44.5992, S: 0.02892 },
  { months: 12, L: 1, M: 44.9227, S: 0.02894 },
  { months: 13, L: 1, M: 45.2118, S: 0.02897 },
  { months: 14, L: 1, M: 45.4712, S: 0.02902 },
  { months: 15, L: 1, M: 45.7046, S: 0.02907 },
  { months: 16, L: 1, M: 45.9148, S: 0.02914 },
  { months: 17, L: 1, M: 46.1051, S: 0.02921 },
  { months: 18, L: 1, M: 46.2784, S: 0.02929 },
  { months: 19, L: 1, M: 46.4373, S: 0.02937 },
  { months: 20, L: 1, M: 46.5837, S: 0.02946 },
  { months: 21, L: 1, M: 46.7189, S: 0.02955 },
  { months: 22, L: 1, M: 46.8449, S: 0.02964 },
  { months: 23, L: 1, M: 46.9633, S: 0.02974 },
  { months: 24, L: 1, M: 47.0748, S: 0.02984 },
]

/** WHO Weight-for-age (kg), GIRLS, 0-24 months */
export const WHO_WFA_GIRLS: LmsRow[] = [
  { months: 0,  L: 0.3809,  M: 3.2322,  S: 0.14171 },
  { months: 1,  L: 0.1714,  M: 4.1873,  S: 0.13724 },
  { months: 2,  L: 0.0962,  M: 5.1282,  S: 0.13000 },
  { months: 3,  L: 0.0402,  M: 5.8458,  S: 0.12619 },
  { months: 4,  L: -0.0050, M: 6.4237,  S: 0.12402 },
  { months: 5,  L: -0.0430, M: 6.8985,  S: 0.12274 },
  { months: 6,  L: -0.0756, M: 7.2970,  S: 0.12204 },
  { months: 7,  L: -0.1039, M: 7.6422,  S: 0.12178 },
  { months: 8,  L: -0.1288, M: 7.9487,  S: 0.12181 },
  { months: 9,  L: -0.1507, M: 8.2254,  S: 0.12199 },
  { months: 10, L: -0.1707, M: 8.4800,  S: 0.12223 },
  { months: 11, L: -0.1885, M: 8.7192,  S: 0.12247 },
  { months: 12, L: -0.2048, M: 8.9481,  S: 0.12268 },
  { months: 13, L: -0.2193, M: 9.1699,  S: 0.12283 },
  { months: 14, L: -0.2325, M: 9.3870,  S: 0.12294 },
  { months: 15, L: -0.2444, M: 9.6008,  S: 0.12299 },
  { months: 16, L: -0.2551, M: 9.8120,  S: 0.12303 },
  { months: 17, L: -0.2650, M: 10.0208, S: 0.12306 },
  { months: 18, L: -0.2740, M: 10.2271, S: 0.12309 },
  { months: 19, L: -0.2825, M: 10.4308, S: 0.12315 },
  { months: 20, L: -0.2903, M: 10.6317, S: 0.12323 },
  { months: 21, L: -0.2977, M: 10.8298, S: 0.12335 },
  { months: 22, L: -0.3046, M: 11.0250, S: 0.12350 },
  { months: 23, L: -0.3111, M: 11.2170, S: 0.12369 },
  { months: 24, L: -0.3172, M: 11.4059, S: 0.12390 },
]

/** Convert LMS + z-score to a value on the reference scale (kg / cm / etc). */
export function lmsValueAtZ(row: LmsRow, z: number): number {
  if (Math.abs(row.L) < 1e-9) return row.M * Math.exp(row.S * z)
  return row.M * Math.pow(1 + row.L * row.S * z, 1 / row.L)
}

/** Linear-interpolate LMS rows to any age in months (fractional). */
export function lmsAtMonths(table: LmsRow[], months: number): LmsRow | null {
  if (months < table[0].months || months > table[table.length - 1].months) return null
  const lo = table[Math.floor(months)] ?? table[0]
  const hi = table[Math.ceil(months)] ?? table[table.length - 1]
  if (lo === hi) return lo
  const t = months - lo.months
  return {
    months,
    L: lo.L + (hi.L - lo.L) * t,
    M: lo.M + (hi.M - lo.M) * t,
    S: lo.S + (hi.S - lo.S) * t,
  }
}

export const P3  = -1.881
export const P15 = -1.036
export const P50 = 0
export const P85 = 1.036
export const P97 = 1.881

/** Compute z-score for an observed value at a given age from LMS. */
export function zScore(table: LmsRow[], months: number, value: number): number | null {
  const row = lmsAtMonths(table, months)
  if (!row) return null
  if (Math.abs(row.L) < 1e-9) return Math.log(value / row.M) / row.S
  return (Math.pow(value / row.M, row.L) - 1) / (row.L * row.S)
}

/** Convert z-score to percentile (0..100). */
export function zToPercentile(z: number): number {
  // Normal CDF approximation (Abramowitz & Stegun 26.2.17)
  const p = 0.5 * (1 + erf(z / Math.SQRT2))
  return p * 100
}

function erf(x: number): number {
  const a1 =  0.254829592
  const a2 = -0.284496736
  const a3 =  1.421413741
  const a4 = -1.453152027
  const a5 =  1.061405429
  const p  =  0.3275911
  const sign = x < 0 ? -1 : 1
  const ax = Math.abs(x)
  const t = 1 / (1 + p * ax)
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-ax * ax)
  return sign * y
}
